import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Sparkles, Film, X, Trash2, Check, ArrowRight, Edit3 } from "lucide-react";
import { toast } from "sonner";
import Markdown from "../components/Markdown";
import { EmojiSelect } from "../components/ui/emoji-select";

const CTA_TYPES = [
  { id: "comentar", label: "💬 Comentar (pregunta abierta)" },
  { id: "compartir", label: "🔁 Compartir (etiquetar amigo)" },
  { id: "save", label: "🔖 Guardar (info valiosa)" },
  { id: "click_link", label: "🔗 Click al link (curiosidad)" },
  { id: "dm", label: "📩 DM (descuento privado)" },
  { id: "follow", label: "➕ Seguir (parte 2 pendiente)" },
  { id: "swipe_up", label: "👆 Swipe up (story)" },
  { id: "ir_app", label: "📱 Ir a la app Rubí" },
  { id: "comprar", label: "🛒 Comprar AHORA (urgencia)" },
];
const HOOK_TYPES = [
  { id: "curiosidad", label: "🤔 Curiosidad" },
  { id: "shock", label: "💥 Shock / Contraintuitivo" },
  { id: "promesa", label: "🎯 Promesa de resultado" },
  { id: "pregunta", label: "❓ Pregunta directa" },
  { id: "story", label: "📖 Storytelling" },
  { id: "polemica", label: "🔥 Polémica" },
  { id: "dato", label: "📊 Dato sorprendente" },
  { id: "comparativa", label: "⚖️ Comparativa" },
  { id: "beneficio_inmediato", label: "⚡ Beneficio inmediato" },
];
const EMOTIONAL_DRIVERS = [
  { id: "aspiracion", label: "✨ Aspiración" },
  { id: "FOMO", label: "😱 FOMO" },
  { id: "status", label: "👑 Status" },
  { id: "comedia", label: "😂 Comedia" },
  { id: "miedo", label: "😰 Miedo / Inseguridad" },
  { id: "nostalgia", label: "🥲 Nostalgia" },
  { id: "controversia", label: "⚡ Controversia" },
  { id: "validacion", label: "🙌 Validación social" },
  { id: "comunidad", label: "🤝 Comunidad" },
];
const BRAND_PILLARS = [
  { id: "educar", label: "📚 Educar" },
  { id: "entretener", label: "🎭 Entretener" },
  { id: "inspirar", label: "✨ Inspirar" },
  { id: "vender", label: "💸 Vender" },
  { id: "comunidad", label: "👥 Comunidad" },
];
const VOICE_MODES = [
  { id: "on_camera", label: "🎤 En cámara" },
  { id: "voice_over", label: "🎙 Voice over + b-roll" },
  { id: "b_roll", label: "🎞 Solo b-roll + texto" },
  { id: "mixed", label: "🎬 Mixto" },
];

const PLATFORMS = ["instagram", "tiktok", "facebook", "youtube_shorts", "multi"];
const FORMATS = [
  { id: "reel", label: "Reel (vertical 9:16)" },
  { id: "post", label: "Post (estático)" },
  { id: "carrusel", label: "Carrusel" },
  { id: "story", label: "Story" },
  { id: "video_horizontal", label: "Video horizontal (16:9)" },
  { id: "video_largo", label: "Video largo" },
  { id: "live", label: "Live" },
];
const FUNNEL_STAGES = [
  { id: "frio", label: "🔵 Cliente frío (descubre la marca)" },
  { id: "tibio", label: "🟡 Cliente tibio (conoce, no compra)" },
  { id: "caliente", label: "🔴 Cliente caliente (listo para comprar)" },
  { id: "fidelizacion", label: "💎 Fidelización (ya compró)" },
];

const STATUS_COLORS = {
  borrador: "pill-gray",
  en_revision: "pill-blue",
  aprobado: "pill-green",
  grabado: "pill-yellow",
  publicado: "pill-dark",
};

const Scripts = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [params] = useSearchParams();

  useEffect(() => {
    if (params.get("new") === "1") setShowForm(true);
  }, [params]);

  const load = () => {
    setLoading(true);
    api.get("/scripts").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <Layout
      title="Guiones"
      subtitle="Aura como director de guion · genera 4 ideas y aprueba la mejor"
      actions={
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="new-script-btn">
          <Sparkles size={14} /> Generar 4 ideas
        </button>
      }
    >
      {loading ? (
        <div className="flex justify-center py-20"><span className="spinner" /></div>
      ) : items.length === 0 ? (
        <div className="aura-card-flat p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#D99D26]/15 text-[#8a5e10] flex items-center justify-center mx-auto mb-4">
            <Film size={24} />
          </div>
          <h3 className="font-display text-2xl font-medium">Sin guiones aún</h3>
          <p className="text-[#666] mt-2 mb-5 max-w-md mx-auto">
            Describe la idea, plataforma, formato y etapa del funnel. Aura genera 4 ideas distintas adaptadas al estilo del creador.
          </p>
          <button onClick={() => setShowForm(true)} className="btn-primary">Generar primer guion</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((s) => (
            <div key={s.id} className="aura-card p-5 group relative cursor-pointer" onClick={() => setSelected(s)} data-testid={`script-${s.id}`}>
              <div className="flex justify-between items-start mb-2">
                <div className="flex gap-1 flex-wrap">
                  <span className="pill pill-dark">{s.platform}</span>
                  {s.format && <span className="pill pill-gray text-[10px]">{s.format}</span>}
                  {s.funnel_stage && <span className="pill pill-yellow text-[10px]">{s.funnel_stage}</span>}
                </div>
                <span className={`pill ${STATUS_COLORS[s.status] || "pill-gray"}`}>{s.status}</span>
              </div>
              <h3 className="font-display text-lg font-medium leading-tight">{s.title}</h3>
              {s.hook && <p className="text-sm text-[#666] mt-2 line-clamp-2"><span className="font-medium text-[#0A0A0A]">Hook:</span> {s.hook}</p>}
              <div className="flex gap-2 mt-3 text-xs text-[#666]">
                {s.duration && <span>{s.duration}</span>}
                {s.creator_name && <span>· {s.creator_name}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && <ScriptIdeasFlow onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      {selected && <ScriptDetail script={selected} onClose={() => setSelected(null)} onChange={() => { setSelected(null); load(); }} />}
    </Layout>
  );
};

const ScriptIdeasFlow = ({ onClose, onCreated }) => {
  const [step, setStep] = useState(1); // 1 = brief form, 2 = pick idea, 3 = edit & approve
  const [form, setForm] = useState({
    brief: "",
    platform: "instagram",
    format: "reel",
    funnel_stage: "frio",
    duration: "30s",
    creator_id: "",
    creator_name: "",
    creator_style: "",
    campaign_id: "",
    objective: "",
    product: "",
    branch: "",
    // Estratégicos
    cta_type: "comentar",
    hook_type: "curiosidad",
    emotional_driver: "aspiracion",
    brand_pillar: "vender",
    voice_mode: "on_camera",
    visual_style: "",
    music_trend: "",
    posting_window: "",
    target_demographic: "",
    references: "",
    engagement_seed: "",
    promo_code: "",
  });
  const [generating, setGenerating] = useState(false);
  const [ideas, setIdeas] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [editedTitle, setEditedTitle] = useState("");
  const [editedContent, setEditedContent] = useState("");
  const [scheduledDate, setScheduledDate] = useState(new Date().toISOString().slice(0, 10));
  const [scheduledTime, setScheduledTime] = useState("12:00");
  const [users, setUsers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  }, []);

  const generateIdeas = async () => {
    if (!form.brief.trim()) { toast.error("Describe el brief/idea base"); return; }
    setGenerating(true);
    try {
      const res = await api.post("/workflow/scripts/generate-ideas", { ...form, num_ideas: 4 });
      setIdeas(res.data.ideas || []);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al generar ideas");
    } finally {
      setGenerating(false);
    }
  };

  const pickIdea = (idea) => {
    setChosen(idea);
    setEditedTitle(idea.title);
    setEditedContent(idea.content);
    setStep(3);
  };

  const approveAndSchedule = async () => {
    if (!editedTitle.trim() || !editedContent.trim()) { toast.error("Título y contenido requeridos"); return; }
    setSaving(true);
    try {
      const res = await api.post("/workflow/scripts/approve-idea", {
        title: editedTitle,
        content: editedContent,
        platform: form.platform,
        format: form.format,
        duration: form.duration,
        funnel_stage: form.funnel_stage,
        creator_id: form.creator_id || null,
        creator_name: form.creator_name,
        campaign_id: form.campaign_id || null,
        scheduled_date: scheduledDate,
        scheduled_time: scheduledTime,
      });
      toast.success(`Guion aprobado y enviado al calendario · ${res.data.task?.assignee_name ? "Notificado a " + res.data.task.assignee_name : "Tarea creada"}`);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto p-7">
        <div className="flex justify-between items-start mb-5">
          <div>
            <div className="label-overline">Aura · Director de guion</div>
            <h2 className="font-display text-2xl font-medium">
              {step === 1 ? "Generar 4 ideas" : step === 2 ? "Elige tu idea favorita" : "Edita y aprueba"}
            </h2>
          </div>
          <button onClick={onClose} data-testid="close-script-flow"><X size={18} /></button>
        </div>

        {/* Steps indicator */}
        <div className="flex gap-2 mb-6">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`flex-1 h-1.5 rounded-full ${step >= s ? "bg-[#D99D26]" : "bg-[#F0F0F0]"}`} />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="aura-label">Brief / Idea base *</label>
              <textarea
                className="aura-input"
                rows="4"
                value={form.brief}
                onChange={(e) => setForm({ ...form, brief: e.target.value })}
                placeholder="Reel mostrando perfume árabe nuevo. Hook conectado con la curiosidad..."
                data-testid="script-brief"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="aura-label">Plataforma</label>
                <select className="aura-input" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} data-testid="script-platform">
                  {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="aura-label">Formato</label>
                <select className="aura-input" value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} data-testid="script-format">
                  {FORMATS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
              </div>
              <div>
                <label className="aura-label">Etapa del funnel</label>
                <select className="aura-input" value={form.funnel_stage} onChange={(e) => setForm({ ...form, funnel_stage: e.target.value })} data-testid="script-funnel">
                  {FUNNEL_STAGES.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
              </div>
              <div>
                <label className="aura-label">Duración</label>
                <input className="aura-input" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="30s" />
              </div>
              <div>
                <label className="aura-label">Asignar creador</label>
                <EmojiSelect
                  value={form.creator_id || "_none_"}
                  onChange={(v) => {
                    if (v === "_none_") { setForm({ ...form, creator_id: "", creator_name: "" }); return; }
                    const u = users.find((x) => x.id === v);
                    setForm({ ...form, creator_id: v, creator_name: u?.name || "" });
                  }}
                  options={[{ id: "_none_", label: "— Sin asignar —" }, ...users.map((u) => ({ id: u.id, label: `${u.name} · ${u.role}` }))]}
                  testid="script-creator"
                />
              </div>
              <div>
                <label className="aura-label">Campaña</label>
                <EmojiSelect
                  value={form.campaign_id || "_none_"}
                  onChange={(v) => setForm({ ...form, campaign_id: v === "_none_" ? "" : v })}
                  options={[{ id: "_none_", label: "— Sin campaña —" }, ...campaigns.map((c) => ({ id: c.id, label: c.name }))]}
                />
              </div>
              <div>
                <label className="aura-label">Producto / Promoción</label>
                <input className="aura-input" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder="Perfumes árabes" />
              </div>
              <div>
                <label className="aura-label">Sucursal / Canal</label>
                <input className="aura-input" value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} placeholder="Tampico, App, Web..." />
              </div>
              <div className="col-span-2">
                <label className="aura-label">Objetivo</label>
                <input className="aura-input" value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} placeholder="Generar awareness antes del fin de semana" />
              </div>
              <div className="col-span-2">
                <label className="aura-label">Notas extras sobre el estilo del creador (opcional, si no tiene perfil completo)</label>
                <input className="aura-input" value={form.creator_style} onChange={(e) => setForm({ ...form, creator_style: e.target.value })} placeholder="Voz cálida, cercana, frases en regional..." />
              </div>
            </div>

            {/* Estrategia de Conversión */}
            <div className="aura-card-flat p-4 bg-gradient-to-br from-[#FFF9E6] to-white border border-[#D99D26]/20 mt-4">
              <div className="label-overline mb-3">🎯 Estrategia de Conversión (Hook viral + CTA personalizado)</div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="aura-label">Tipo de CTA *</label>
                  <EmojiSelect value={form.cta_type} onChange={(v) => setForm({ ...form, cta_type: v })} options={CTA_TYPES} testid="cta-type" />
                </div>
                <div>
                  <label className="aura-label">Tipo de Hook *</label>
                  <EmojiSelect value={form.hook_type} onChange={(v) => setForm({ ...form, hook_type: v })} options={HOOK_TYPES} testid="hook-type" />
                </div>
                <div>
                  <label className="aura-label">Driver emocional</label>
                  <EmojiSelect value={form.emotional_driver} onChange={(v) => setForm({ ...form, emotional_driver: v })} options={EMOTIONAL_DRIVERS} testid="emotional-driver" />
                </div>
                <div>
                  <label className="aura-label">Pilar de marca</label>
                  <EmojiSelect value={form.brand_pillar} onChange={(v) => setForm({ ...form, brand_pillar: v })} options={BRAND_PILLARS} />
                </div>
                <div>
                  <label className="aura-label">Modo de voz</label>
                  <EmojiSelect value={form.voice_mode} onChange={(v) => setForm({ ...form, voice_mode: v })} options={VOICE_MODES} />
                </div>
                <div>
                  <label className="aura-label">Estilo visual</label>
                  <input className="aura-input" value={form.visual_style} onChange={(e) => setForm({ ...form, visual_style: e.target.value })} placeholder="Aesthetic, raw, cinematic..." />
                </div>
                <div>
                  <label className="aura-label">Audio/trend musical</label>
                  <input className="aura-input" value={form.music_trend} onChange={(e) => setForm({ ...form, music_trend: e.target.value })} placeholder="Trend X de TikTok / instrumental..." />
                </div>
                <div>
                  <label className="aura-label">Ventana de publicación</label>
                  <input className="aura-input" value={form.posting_window} onChange={(e) => setForm({ ...form, posting_window: e.target.value })} placeholder="Viernes 19:00-21:00" />
                </div>
                <div className="col-span-2">
                  <label className="aura-label">Target demográfico específico</label>
                  <input className="aura-input" value={form.target_demographic} onChange={(e) => setForm({ ...form, target_demographic: e.target.value })} placeholder="Hombres 25-35 Tampico, ingreso medio, busca regalo Día Padre" />
                </div>
                <div>
                  <label className="aura-label">Engagement seed (1er comentario)</label>
                  <input className="aura-input" value={form.engagement_seed} onChange={(e) => setForm({ ...form, engagement_seed: e.target.value })} placeholder="'cuéntame cuál es tu favorito 👇'" />
                </div>
                <div>
                  <label className="aura-label">Código promo (tracking)</label>
                  <input className="aura-input" value={form.promo_code} onChange={(e) => setForm({ ...form, promo_code: e.target.value })} placeholder="TERE20" />
                </div>
                <div className="col-span-2">
                  <label className="aura-label">Referencias / inspiraciones (URLs, ideas)</label>
                  <textarea className="aura-input" rows="2" value={form.references} onChange={(e) => setForm({ ...form, references: e.target.value })} placeholder="https://...&#10;Reel similar de @creator..." />
                </div>
              </div>
            </div>

            <button onClick={generateIdeas} disabled={generating} className="btn-primary w-full justify-center flex items-center gap-2" data-testid="generate-ideas-btn">
              {generating ? <><span className="spinner" /> Aura generando 4 ideas distintas...</> : <><Sparkles size={14} /> Generar 4 ideas</>}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-[#666]">Aura generó {ideas.length} ideas distintas. Selecciona la que más te guste y podrás editarla antes de aprobar.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {ideas.map((idea, i) => (
                <div
                  key={idea.id}
                  className="aura-card p-5 cursor-pointer hover:border-[#D99D26] hover:shadow-md transition-all"
                  onClick={() => pickIdea(idea)}
                  data-testid={`idea-${i}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <span className="pill pill-yellow text-[10px]">Idea {i + 1}</span>
                    <ArrowRight size={14} className="text-[#8a5e10]" />
                  </div>
                  <h3 className="font-display text-base font-medium mb-2">{idea.title}</h3>
                  <div className="text-xs text-[#666] line-clamp-6 markdown-content">
                    <Markdown content={idea.content.slice(0, 500)} />
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setStep(1)} className="btn-ghost">← Volver</button>
          </div>
        )}

        {step === 3 && chosen && (
          <div className="space-y-4">
            <div>
              <label className="aura-label">Título del guion</label>
              <input className="aura-input" value={editedTitle} onChange={(e) => setEditedTitle(e.target.value)} data-testid="edited-title" />
            </div>
            <div>
              <label className="aura-label flex items-center gap-1"><Edit3 size={12} /> Contenido del guion (editable)</label>
              <textarea className="aura-input font-mono text-sm" rows="14" value={editedContent} onChange={(e) => setEditedContent(e.target.value)} data-testid="edited-content" />
            </div>

            <ViralPredictor
              content={editedContent}
              platform={form.platform}
              format={form.format}
              funnel_stage={form.funnel_stage}
              creator_id={form.creator_id}
              hook_type={form.hook_type}
              cta_type={form.cta_type}
            />

            <div className="grid grid-cols-2 gap-4 border-t border-[#E5E5E5] pt-4">
              <div>
                <label className="aura-label">Fecha de publicación</label>
                <input type="date" className="aura-input" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} data-testid="scheduled-date" />
              </div>
              <div>
                <label className="aura-label">Hora</label>
                <input type="time" className="aura-input" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} />
              </div>
            </div>
            <div className="aura-card-flat p-4 bg-[#FFF9E6]/40 text-sm text-[#666]">
              <strong className="text-[#0A0A0A]">Al aprobar:</strong> el guion se guarda como <em>aprobado</em>, se agrega al calendario editorial y se crea una tarea para <strong>{form.creator_name || "el creador asignado"}</strong>.
            </div>
            <div className="flex justify-between gap-2">
              <button onClick={() => setStep(2)} className="btn-ghost">← Cambiar idea</button>
              <button onClick={approveAndSchedule} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="approve-script-btn">
                {saving ? <><span className="spinner" /> Guardando...</> : <><Check size={14} /> Aprobar y enviar al calendario</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ScriptDetail = ({ script, onClose, onChange }) => {
  const [status, setStatus] = useState(script.status);
  const updateStatus = async (s) => {
    setStatus(s);
    await api.patch(`/scripts/${script.id}`, { status: s });
    toast.success("Estado actualizado");
  };
  const remove = async () => {
    if (!window.confirm("¿Eliminar guion?")) return;
    await api.delete(`/scripts/${script.id}`);
    toast.success("Eliminado");
    onChange();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-7">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="label-overline mb-1">{script.platform} · {script.format || "—"} · {script.duration} · funnel: {script.funnel_stage || "—"}</div>
            <h2 className="font-display text-2xl font-medium">{script.title}</h2>
            {script.creator_name && <div className="text-sm text-[#666] mt-1">Asignado a {script.creator_name}</div>}
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="flex gap-2 mb-4 flex-wrap">
          {["borrador", "en_revision", "aprobado", "grabado", "publicado"].map((s) => (
            <button key={s} onClick={() => updateStatus(s)} className={`pill cursor-pointer ${status === s ? "pill-yellow" : "pill-gray"}`}>
              {status === s && <Check size={10} />} {s}
            </button>
          ))}
        </div>
        <div className="aura-card-flat p-5">
          <Markdown content={script.raw_ai_output || ""} />
        </div>
        <div className="flex justify-between mt-5">
          <button onClick={remove} className="btn-ghost text-red-600 hover:text-red-700">
            <Trash2 size={14} className="inline mr-1" /> Eliminar
          </button>
          <button onClick={() => { navigator.clipboard.writeText(script.raw_ai_output || ""); toast.success("Copiado"); }} className="btn-outline">
            Copiar guion completo
          </button>
        </div>
      </div>
    </div>
  );
};

const ViralPredictor = ({ content, platform, format, funnel_stage, creator_id, hook_type, cta_type }) => {
  const [loading, setLoading] = useState(false);
  const [pred, setPred] = useState(null);

  const predict = async () => {
    setLoading(true);
    try {
      const res = await api.post("/command/predict-viral", { content, platform, format, funnel_stage, creator_id, hook_type, cta_type });
      setPred(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  const p = pred?.prediction || {};
  const score = p.score;
  const scoreColor = score >= 8 ? "text-green-700 bg-green-50 border-green-300" : score >= 5 ? "text-amber-700 bg-amber-50 border-amber-300" : score ? "text-red-700 bg-red-50 border-red-300" : "";

  return (
    <div className="aura-card-flat p-4 bg-gradient-to-br from-[#FFF9E6] to-white border border-[#D99D26]/30">
      <div className="flex justify-between items-center mb-2">
        <div>
          <div className="label-overline">🧠 Predicción de viralidad</div>
          <p className="text-xs text-[#666] mt-0.5">Aura puntúa este guion ANTES de publicarlo (basado en tu histórico marcado)</p>
        </div>
        <button onClick={predict} disabled={loading || !content?.trim()} type="button" className="btn-outline text-xs flex items-center gap-1" data-testid="predict-viral-btn">
          {loading ? <><span className="spinner" /> Aura evaluando...</> : <><Sparkles size={11} /> {pred ? "Re-evaluar" : "Predecir"}</>}
        </button>
      </div>
      {pred && (
        <div className="space-y-3 mt-3">
          <div className="flex items-center gap-4">
            <div className={`w-20 h-20 rounded-2xl border-2 flex flex-col items-center justify-center ${scoreColor}`}>
              <div className="font-display text-4xl font-bold leading-none">{score ?? "?"}</div>
              <div className="text-[9px] uppercase tracking-wider">/10</div>
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium">{p.verdict?.replace(/_/g, " ") || "—"}</div>
              <div className="text-xs text-[#666]">{p.predicted_reach_bucket && `Reach: ${p.predicted_reach_bucket}`} {p.predicted_engagement_range && `· Engagement: ${p.predicted_engagement_range}`}</div>
              <div className="text-[10px] text-[#666] mt-1">📈 {pred.disclaimer}</div>
            </div>
          </div>
          {p.strengths?.length > 0 && (
            <div>
              <div className="text-[10px] font-medium text-green-700">✅ Fortalezas</div>
              <ul className="text-xs list-disc pl-5">{p.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </div>
          )}
          {p.weaknesses?.length > 0 && (
            <div>
              <div className="text-[10px] font-medium text-red-700">⚠️ Debilidades</div>
              <ul className="text-xs list-disc pl-5">{p.weaknesses.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </div>
          )}
          {p.recommendations?.length > 0 && (
            <div>
              <div className="text-[10px] font-medium text-[#8a5e10]">💡 Recomendaciones de Aura</div>
              <ul className="text-xs list-disc pl-5">{p.recommendations.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </div>
          )}
          {p.best_post_time_suggestion && (
            <div className="text-xs text-[#666]">🕐 <strong>Mejor hora sugerida:</strong> {p.best_post_time_suggestion}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default Scripts;
